import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState } from "react";
import { coordPattern } from "./utils";

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface ZipCodeLocation extends Coordinates {
  zipCode: string;
}

/**
 * Hook to geocode zip code to lat/lng coordinates.
 * Automatically handles cleanup and prevents stale results.
 * Must be used inside an APIProvider component.
 */
export function useZipCodeGeocode(zipCode: string | null | undefined) {
  const [zipCodeLocation, setZipCodeLocation] =
    useState<ZipCodeLocation | null>(null);
  const geocodingLib = useMapsLibrary("geocoding");
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!zipCode || !geocodingLib) {
      setZipCodeLocation(null);
      return;
    }

    // Clear previous location immediately when zip code changes
    setZipCodeLocation(null);

    // Increment request ID to invalidate stale requests
    const currentRequestId = ++requestIdRef.current;
    const geocoder = new geocodingLib.Geocoder();

    geocoder.geocode(
      { address: zipCode, componentRestrictions: { country: "US" } },
      (results, status) => {
        // Only set location if this is still the latest request
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        if (status === "OK" && results?.[0]) {
          const location = results[0].geometry.location;
          setZipCodeLocation({
            lat: location.lat(),
            lng: location.lng(),
            zipCode,
          });
        } else {
          setZipCodeLocation(null);
        }
      },
    );
  }, [zipCode, geocodingLib]);

  // Only return location if it matches the current zip code
  return zipCodeLocation?.zipCode === zipCode ? zipCodeLocation : null;
}

/**
 * Calculates the distance between two coordinates using the Haversine formula.
 * Returns the distance in miles.
 *
 * @param coord1 - First coordinate (lat, lng)
 * @param coord2 - Second coordinate (lat, lng)
 * @returns Distance in miles
 */
export function calculateDistance(
  coord1: Coordinates,
  coord2: Coordinates,
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRadians(coord2.lat - coord1.lat);
  const dLon = toRadians(coord2.lng - coord1.lng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(coord1.lat)) *
      Math.cos(toRadians(coord2.lat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Converts degrees to radians.
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Formats distance for display.
 * Shows miles with one decimal place, or "< 0.1 mi" for very short distances.
 *
 * @param distanceInMiles - Distance in miles
 * @returns Formatted distance string
 */
export function formatDistance(distanceInMiles: number): string {
  if (distanceInMiles < 0.1) {
    return "< 0.1 mi";
  }
  return `${distanceInMiles.toFixed(1)} mi`;
}

// In utils.ts or helpers.ts
export function getLocationCoordinates(
  googleMapsEmbed: string,
): Coordinates | null {
  const match = googleMapsEmbed.match(coordPattern);
  if (!match) return null;
  const [, longitude, latitude] = match;
  return {
    lat: Number.parseFloat(latitude),
    lng: Number.parseFloat(longitude),
  };
}
